<template lang="pug">
v-footer#home-footer(theme='dark', min-height='72')
  v-container
    v-row(justify='space-between', align='center')
      v-col(cols='12', md='7')
        .footer-copy
          base-display-title(
            title='Enterprise of Things Lab'
            size='compact'
            font='sans'
            tone='subtle'
            :arrow='true'
            class='footer-title'
          )
          p.footer-meta
            | {{ t('footer.copyright', { year: currentYear }) }} 
            nuxt-link(:to='localePath("/privacy")') {{ t('footer.privacy') }}
          a.footer-affiliation(
            :href='researchGroupUrl'
            target='_blank'
            rel='noopener noreferrer'
            :aria-label='t("footer.affiliationLabel")'
          )
            span.footer-kicker {{ t('footer.kicker') }}
            strong {{ t('footer.university') }}
          LanguageSwitcher.footer-language-switcher
      v-col(cols='12', md='4')
        a.footer-logo-link(
          :href='researchGroupUrl'
          target='_blank'
          rel='noopener noreferrer'
          :aria-label='t("footer.logoLabel")'
        )
          NuxtPicture.footer-logo(
            :src='universityLogo'
            alt=''
            width='954'
            height='297'
            sizes='xs:260px md:288px'
            fit='contain'
            :img-attrs='{ class: "footer-logo-image" }'
          )
        nav.footer-social-links(:aria-label='t("footer.socialLabel")')
          a.footer-social-link(
            v-for='link in formattedSocialLinks'
            :key='link.label'
            :href='link.href'
            target='_blank'
            rel='noopener noreferrer'
          )
            span.footer-social-mark(v-if='link.mark', aria-hidden='true') {{ link.mark }}
            v-icon(v-else, size='18', aria-hidden='true') {{ link.icon }}
            span {{ link.label }}
</template>

<script setup>
import { formatExternalProfileLinks } from '~/utils/externalProfiles'

defineOptions({
  name: 'HomeFooter',
})

const currentYear = new Date().getFullYear()
const { t } = useI18n()
const localePath = useLocalePath()
const researchGroupUrl = 'https://www.uni-koblenz.de/de/informatik/iwvi/riehle/'
const universityLogo = '/uni-ko/UK_Logo_Englisch_weiß.png'
const socialLinks = [
  {
    title: 'ResearchGate',
    link: 'https://www.researchgate.net/lab/EoTLab-Dennis-M-Riehle',
  },
  {
    title: 'LinkedIn',
    link: 'https://www.linkedin.com/company/101797331',
  },
  {
    title: 'SciPort RLP',
    link: 'https://www.rlp-forschung.de/public/facilities/3226',
  },
]
const formattedSocialLinks = formatExternalProfileLinks(socialLinks).map(
  (link) => ({
    ...link,
    label: link.title,
    href: link.link,
  }),
)
</script>

<style scoped>
#home-footer {
  background:
    linear-gradient(
      180deg,
      color-mix(in srgb, var(--color-brand-dark) 92%, #24303b) 0%,
      var(--color-brand-dark) 100%
    ),
    radial-gradient(
      circle at top right,
      rgba(0, 254, 254, 0.12),
      transparent 24rem
    );
  border-top: 3px solid color-mix(in srgb, var(--color-brand-accent) 78%, white);
  padding: 1.4rem 0;
}

.footer-copy {
  display: grid;
  gap: 0.65rem;
}

.footer-meta {
  margin: 0;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.6;
}

.footer-meta a {
  color: white;
  text-decoration-color: rgba(255, 255, 255, 0.44);
}

.footer-social-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem 0.85rem;
  align-items: center;
  justify-content: flex-end;
  margin-top: 1rem;
}

.footer-social-link {
  display: inline-flex;
  gap: 0.4rem;
  align-items: center;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.2;
  text-decoration: none;
}

.footer-social-link:hover,
.footer-social-link:focus-visible {
  text-decoration: underline;
  text-decoration-color: rgba(255, 255, 255, 0.55);
  text-underline-offset: 0.18em;
}

.footer-social-mark {
  display: inline-grid;
  width: 1.15rem;
  height: 1.15rem;
  place-items: center;
  border: 1px solid currentColor;
  border-radius: 0.12rem;
  font-size: 0.55rem;
  font-weight: 800;
  line-height: 1;
}

.footer-affiliation {
  display: inline-grid;
  gap: 0.15rem;
  color: white;
  text-decoration: none;
  justify-self: start;
}

.footer-language-switcher {
  justify-self: start;
  color: white;
}

.footer-kicker {
  color: rgba(255, 255, 255, 0.76);
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 0.72rem;
  font-weight: 700;
}

.footer-title {
  color: rgba(255, 255, 255, 0.94);
}

.footer-title:deep(.base-display-title__arrow path) {
  fill: var(--brand-primary);
}

.footer-logo-link {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.footer-logo {
  display: block;
  width: min(100%, 18rem);
}

.footer-logo :deep(picture) {
  display: block;
}

.footer-logo :deep(.footer-logo-image) {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
}

@media (max-width: 960px) {
  .footer-logo-link {
    justify-content: flex-start;
    margin-top: 0.8rem;
  }

  .footer-social-links {
    justify-content: flex-start;
  }
}
</style>
